import { useEffect, useRef, useState } from 'react';
import Cleave from 'cleave.js/react';
import Button from '../Button/Button';

import ErrorText from '../../Text/ErrorText/ErrorText';
import presets, { TextFieldPresets, TextFieldType, TextFieldPreset } from './TextField.presets';
import styles from './TextField.module.scss';

import { useDatadogLog } from '../logging';

type OnChangeProps<T> = {
  id?: T;
  value: string;
};

export type TextFieldProps<T> = {
  id?: string;
  variant?:
    | 'outlined'
    | 'standard'
    | 'plain'
    | 'background'
    | 'dropdown'
    | 'lined'
    | 'none'
    | 'border'
    | 'noHighlight'
    | 'calendar-header'
    | 'outlineShadow';
  preset?: keyof TextFieldPresets;
  inputClass?: string;
  value?: string | number;
  name: T;
  disabled?: boolean;
  placeholder?: string;
  placeholderColor?: boolean;
  bold?: boolean;
  error?: string | string[] | null;
  errorFitContent?: boolean;
  width?: string;
  inputWidth?: string;
  height?: string;
  creditCard?: boolean;
  security?: boolean;
  borderColor?: string;
  maxLength?: number;
  showcaseLength?: boolean;
  isVisible?: boolean;
  textColor?: 'black' | 'white' | 'grey';
  isActive?: boolean;
  readOnly?: boolean;
  elementLeft?: React.JSX.Element;
  elementRight?: React.JSX.Element | boolean;
  padding?: string;
  isAutoCompleteDisabled?: boolean;
  backgroundColor?: string;
  containerWidth?: string;
  ariaLabel?: string;
  style?: {
    '--textFieldBorderRadius'?: string;
  };
  autoFocus?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, props: OnChangeProps<T>) => void;
  onRefChange?: (ref: React.RefObject<HTMLInputElement | null>) => void;
  onInput?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
};

const TextField = <T extends string>(props: TextFieldProps<T>): React.JSX.Element => {
  const {
    id,
    variant = 'outlined',
    preset = 'text',
    value,
    name,
    disabled = false,
    placeholder,
    placeholderColor,
    bold,
    error = null,
    errorFitContent = false,
    width = '100%',
    inputWidth = '100%',
    height,
    creditCard = false,
    security = false,
    borderColor,
    maxLength,
    isVisible = true,
    textColor = 'black',
    isActive,
    readOnly,
    elementLeft,
    elementRight,
    padding,
    isAutoCompleteDisabled,
    backgroundColor,
    containerWidth,
    ariaLabel,
    style,
    inputClass,
    autoFocus = false,
    onBlur = () => {},
    onChange = () => {},
    onRefChange = () => {},
    onInput = () => {},
    onFocus = () => {},
    onKeyPress = () => {},
    onKeyDown = () => {},
    onPaste = () => {},
  } = props;

  const [cursor, setCursor] = useState<number | null>(null);
  const [, setType] = useState('unknown');
  const [maskedCreditCard, setMaskedCreditCard] = useState<string>('');
  const [maskedCVV, setMaskedCVV] = useState<string>('');
  const [maskedValue, setMaskedValue] = useState('');
  const [displayCardNum, setDisplayCardNum] = useState<boolean>(false);
  const [displayCVV, setDisplayCVV] = useState<boolean>(false);
  const ref = useRef<HTMLInputElement>(null);
  const log = useDatadogLog();
  useEffect(() => {
    onRefChange(ref);
    const input = ref.current;
    if (input && document.activeElement === input) {
      input.setSelectionRange(cursor, cursor);
    }
  }, [ref, cursor, value]);

  useEffect(() => {
    if (preset === 'private') {
      const str = String(value).replace(/./g, '●');
      setMaskedValue(str);
    }
  }, [preset, value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCursor(event.target.selectionStart);
    onChange(event, { id: name, value: event.currentTarget.value });

    if (preset === 'creditCard' && security) {
      const num =
        event.currentTarget.value
          .replace(/\s/g, '')
          .replace(/[0-9]/g, '•')
          .match(/.{1,4}/g)
          ?.join(' ') || '';
      setMaskedCreditCard(num);
    } else if (security) {
      const num = event.currentTarget.value.replace(/[0-9]/g, '•');
      setMaskedCVV(num);
      onChange(event, { id: name, value: num });
    } else if (preset === 'private') {
      const str = event.currentTarget.value.replace(/./g, '●');
      setMaskedValue(str);
    }
  };

  const handleSecurity = (event: React.FocusEvent<HTMLInputElement>) => {
    if (preset === 'creditCard' && security) {
      setDisplayCardNum(true);
    } else if (security) {
      setDisplayCVV(true);
    }
    onBlur(event);
  };

  const handleCreditCardTypeChange = (type: string) => {
    setType(type);
  };

  const handleDisplayCardNum = () => {
    setDisplayCardNum(!displayCardNum);
  };

  const handleDisplayCVV = () => {
    setDisplayCVV(!displayCVV);
  };

  const handleCopy = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (preset === 'private') {
      event.preventDefault();
    }
  };

  if (preset === 'creditCard' && presets[preset].options.creditCard) {
    presets[preset].options.onCreditCardTypeChanged = handleCreditCardTypeChange;
  }

  if (creditCard && preset === 'date' && presets[preset].options.date) {
    presets[preset].options.datePattern = ['m', 'y'];
    presets[preset].options.delimiter = '/';
  } else if (!creditCard && preset === 'date' && presets[preset].options.date) {
    presets[preset].options.datePattern = ['Y', 'm', 'd'];
    presets[preset].options.delimiter = '-';
  }
  const formatCurrency = (formatValue: string) => {
    // Remove non-numeric characters (except decimal points)
    const numericValueString = formatValue.replace(/[^\d.]/g, '');

    // Convert to a float and format to 2 decimal places
    const numericValue = Number(numericValueString);

    // Add commas for thousands (for example, 1234567.89 -> 1,234,567.89)
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    log({
      message: `${value} was typed for ${name}`,
      payload: { name, value },
      actionType: 'Input',
    });
    if (preset === 'currency') {
      const formattedValue = formatCurrency(e.currentTarget.value);
      e.currentTarget.value = formattedValue;
      onChange(e, { id: name, value: formattedValue || '' });
      onBlur(e);
    } else if (security) {
      handleSecurity(e);
    } else {
      onBlur(e);
    }
  };

  return (
    <div
      className={[styles.textFieldContainer, styles[getVisibleClass()]].join(' ')}
      style={{ width: containerWidth, ...style }}
    >
      <div
        className={[
          styles.textField,
          styles[variant],
          styles[getErrorClass()],
          styles[getErrorTextColorClass()],
          styles[getActiveClass()],
          styles[getReadOnlyClass()],
          styles[getDisabledClass()],
        ].join(' ')}
        style={{ width, height, borderColor: `${borderColor}` }}
      >
        {elementLeft}

        {hasMask(presets[preset]) ? (
          <>
            {displayCardNum && <div className={styles.mask}>{maskedCreditCard}</div>}
            {preset === 'private' && <div className={styles.maskedValue}>{maskedValue}</div>}
            <Cleave
              id={id || `textfield-${name}`}
              name={name}
              type={presets[preset].type}
              options={presets[preset].options}
              placeholder={disabled && !placeholder ? '—' : placeholder}
              className={[
                styles.input,
                styles[getPlaceholderColorClass()],
                styles[getBoldClass()],
                styles[getMaskClass()],

                styles[textColor],
                inputClass,
              ].join(' ')}
              style={preset === 'creditCard' ? { width: '93%' } : { width: inputWidth }}
              value={value}
              disabled={disabled}
              readOnly={readOnly}
              maxLength={maxLength}
              onBlur={(e) => {
                handleBlur(e);
              }}
              onChange={handleChange}
              onInput={onInput}
              onFocus={onFocus}
              onCopy={handleCopy}
              data-testid={`textField-${id || name}`}
            />
          </>
        ) : (
          <>
            {displayCardNum && <div className={styles.mask}>{maskedCreditCard}</div>}
            {preset === 'private' && <div className={styles.maskedValue}>{maskedValue}</div>}
            <input
              data-testid={`textField-${id || name}`}
              id={id || `textfield-${name}`}
              ref={ref}
              name={name}
              type={preset === 'password' ? 'password' : 'text'}
              inputMode={presets[preset].type}
              placeholder={preset === 'private' ? '' : disabled && !placeholder ? '—' : placeholder}
              className={[
                styles.input,
                styles[getPlaceholderColorClass()],
                styles[getBoldClass()],
                styles[getMaskClass()],
                styles[textColor],
                inputClass,
              ].join(' ')}
              style={{ width: inputWidth, padding, backgroundColor }}
              value={value}
              disabled={disabled}
              readOnly={readOnly}
              maxLength={maxLength}
              onBlur={handleBlur}
              onChange={handleChange}
              onInput={onInput}
              onFocus={onFocus}
              onCopy={handleCopy}
              onKeyPress={onKeyPress}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              autoComplete={isAutoCompleteDisabled ? 'off' : ''}
              aria-label={ariaLabel}
              autoFocus={autoFocus}
            />
          </>
        )}

        {security && preset === 'creditCard' && maskedCreditCard !== '' ? (
          <Button
            id="security-creditcard-btn"
            label=""
            background="#f5f5f5"
            noShadow
            color="secondary"
            icon={displayCardNum ? 'eyeClose' : 'eyeOpen'}
            iconLocation="right"
            iconWidth="17px"
            onClick={handleDisplayCardNum}
          />
        ) : security && maskedCVV !== '' ? (
          <Button
            id="security-cvv-btn"
            label=""
            background="#f5f5f5"
            noShadow
            color="secondary"
            icon={displayCVV ? 'eyeClose' : 'eyeOpen'}
            iconLocation="right"
            iconWidth="17px"
            onClick={handleDisplayCVV}
          />
        ) : (
          elementRight
        )}
      </div>
      <ErrorText error={error} size="sm" indentation fitContent={errorFitContent} />
    </div>
  );

  function getErrorClass() {
    return error ? 'hasError' : '';
  }

  function getErrorTextColorClass() {
    return error && variant === 'lined' ? 'hasErrorColorText' : '';
  }

  function hasMask(inputType: TextFieldType | TextFieldPreset): inputType is TextFieldPreset {
    return Object.prototype.hasOwnProperty.call(inputType, 'options');
  }

  function getPlaceholderColorClass() {
    return placeholderColor ? 'placeholder-color' : '';
  }

  function getBoldClass() {
    return bold ? 'bold' : '';
  }

  function getVisibleClass() {
    return isVisible ? '' : 'inVisible';
  }

  function getMaskClass() {
    return preset === 'private' ? 'maskedInput' : '';
  }

  function getActiveClass() {
    return isActive ? 'active' : '';
  }

  function getReadOnlyClass() {
    return readOnly ? 'readOnly' : '';
  }
  function getDisabledClass() {
    return disabled ? 'disabled' : '';
  }
};

export default TextField;
